import { Box, Stack, Typography } from '@mui/material';
import { first } from 'lodash';
import { useMemo } from 'react';
import { Path, useWatch } from 'react-hook-form';

import { Link } from '@/components/form/baseInputs/Link';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareFormattedNumberInput } from '@/components/form/formAwareInputs/FormAwareFormattedNumberInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import { useModalState } from '@/hooks/useModalState';
import { ClientProfileSideDrawer } from '@/modules/clientProfiles/ClientProfileForm/ClientProfileModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { AccessParameterKind } from '@/types/schema';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import { BeneficiaryFormAccessParameter } from './EntityBeneficiariesSubform.types';
import { useAgeCheckQuery } from './graphql/AgeCheck.generated';

type AccessParameterAgeRequirementFormPaths =
  Path<BeneficiaryFormAccessParameter>;

interface AccessParameterAgeRequirementFormProps {
  fieldName:
    | `values.${AccessParameterKind}` // The values object (HEMS, etc.)
    | `amountsOrPercentages.${number}`; // The amountsOrPercentages array (specific percent or amount)
  beneficiaryId: string;
}

export function AccessParameterAgeRequirementForm({
  fieldName,
  beneficiaryId,
}: AccessParameterAgeRequirementFormProps) {
  const { householdId } = useHouseholdDetailsContext();
  const { control } = useFormContext<BeneficiaryFormAccessParameter>();
  const [{ isModalOpen }, { openModal, closeModal }] = useModalState();
  const [hasAgeRequirementToggled, selectedAgeParamKind] = useWatch({
    name: [
      `${fieldName}._hasAgeParams` as '_hasAgeParams' satisfies AccessParameterAgeRequirementFormPaths,
      `${fieldName}.ageParams.0._ageRequirementKind` as `ageParams.${number}._ageRequirementKind` satisfies AccessParameterAgeRequirementFormPaths,
    ],
    control,
  });
  const [clientProfileId, shouldCheckAge] = useMemo<[string, boolean]>(() => {
    if (!hasAgeRequirementToggled) return ['', false];
    const pulidKind = getPulidKind(beneficiaryId);
    if (pulidKind !== PulidKind.ClientProfile) return ['', false];

    return [beneficiaryId, true];
  }, [beneficiaryId, hasAgeRequirementToggled]);

  const {
    data: ageCheckData,
    loading,
    error,
    refetch,
  } = useAgeCheckQuery({
    variables: {
      clientProfileId,
      shouldCheckAge,
    },
  });

  const hasValidAge = useMemo<boolean>(() => {
    if (!shouldCheckAge) return true;
    if (error || loading) return false;

    return !!first(getNodes(ageCheckData?.clientProfiles))?.dateOfBirth;
  }, [ageCheckData?.clientProfiles, error, loading, shouldCheckAge]);

  return (
    <Card
      variant="filled"
      sx={{
        borderRadius: '6px',
        bgcolor: 'transparent',
      }}
    >
      <FormAwareCheckbox<BeneficiaryFormAccessParameter>
        label="Age requirement"
        fieldName={
          `${fieldName}._hasAgeParams` as '_hasAgeParams' satisfies AccessParameterAgeRequirementFormPaths
        }
        control={control}
      />
      {hasAgeRequirementToggled && (
        <Card
          variant="filled"
          sx={{
            p: 0,
            mt: 1.5,
            borderRadius: '6px',
            bgcolor: 'transparent',
          }}
        >
          <Stack spacing={0}>
            <Stack spacing={1} direction="row" alignItems="center">
              <Stack minWidth="120px">
                <FormAwareSelectInput<BeneficiaryFormAccessParameter>
                  label="Age requirement"
                  hideLabel
                  fieldName={
                    `${fieldName}.ageParams.${0}._ageRequirementKind` as `ageParams.${number}._ageRequirementKind` satisfies AccessParameterAgeRequirementFormPaths
                  }
                  control={control}
                  options={[
                    { value: 'upon', display: 'Upon' },
                    { value: 'until', display: 'Until' },
                    { value: 'between', display: 'Between' },
                  ]}
                />
              </Stack>
              <Typography variant="body-s" width="30px">
                {selectedAgeParamKind === 'between' ? 'ages' : 'age'}
              </Typography>
              {(selectedAgeParamKind === 'upon' ||
                selectedAgeParamKind === 'between') && (
                <Box width="80px">
                  <FormAwareFormattedNumberInput<BeneficiaryFormAccessParameter>
                    control={control}
                    label="Age"
                    hideLabel
                    fieldName={
                      `${fieldName}.ageParams.${0}.ageRequirementStart` as `ageParams.${number}.ageRequirementStart` satisfies AccessParameterAgeRequirementFormPaths
                    }
                    fixedDecimalScale
                    decimalScale={0}
                    required
                  />
                </Box>
              )}
              {selectedAgeParamKind === 'between' && (
                <Typography variant="body-s">to</Typography>
              )}
              {(selectedAgeParamKind === 'until' ||
                selectedAgeParamKind === 'between') && (
                <Box width="80px">
                  <FormAwareFormattedNumberInput<BeneficiaryFormAccessParameter>
                    control={control}
                    label="Age"
                    hideLabel
                    fieldName={
                      `${fieldName}.ageParams.${0}.ageRequirementEnd` as `ageParams.${number}.ageRequirementEnd` satisfies AccessParameterAgeRequirementFormPaths
                    }
                    fixedDecimalScale
                    decimalScale={0}
                    required
                  />
                </Box>
              )}
            </Stack>
            {!hasValidAge && !loading && (
              <Stack spacing={1} direction="row" alignItems="center">
                <Typography variant="body-xs">
                  This person does not have a date of birth specified
                </Typography>
                <Link display="Add DOB" onClick={openModal} />
              </Stack>
            )}
          </Stack>
        </Card>
      )}
      {isModalOpen && householdId && (
        <ClientProfileSideDrawer
          isOpen={isModalOpen}
          setIsOpen={(isOpen) => {
            if (isOpen) {
              openModal();
            } else {
              void refetch();
              closeModal();
            }
          }}
          clientProfileId={clientProfileId}
          clientProfileTypeDisplay="Beneficiary"
          householdId={householdId}
        />
      )}
    </Card>
  );
}
